import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Key,
  Pencil,
  Trash,
  CaretUp,
  CaretDown,
  CaretUpDown,
  Printer,
  FileCsv,
  FilePdf,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import ContractDialog from "@/contracts/ContractDialog";
import { printCodeCard, printCodesTable, exportCodesCSV } from "./codesExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLS = [
  { key: "customer", label: "ΠΕΛΑΤΗΣ", grow: true, minWidth: "200px" },
  { key: "items", label: "ΤΥΠΟΣ", grow: true, minWidth: "180px", isItems: true },
  { key: "code", label: "ΚΩΔΙΚΟΣ", width: "130px" },
  { key: "phone", label: "ΤΗΛΕΦΩΝΟ", width: "150px" },
  { key: "expiry_date", label: "ΛΗΞΗ", width: "120px", isDate: true },
  { key: "notes", label: "ΣΗΜΕΙΩΣΕΙΣ", grow: true, wide: true },
  { key: "codes", label: "ΚΩΔΙΚΟΙ ΠΡΟΓΡ.", grow: true, wide: true, codes: true },
];

function itemsLabel(c) {
  if (Array.isArray(c.items) && c.items.length) {
    return c.items
      .map((it) => (it.qty > 1 ? `${it.kind} x${it.qty}` : it.kind))
      .join(" + ");
  }
  return c.kind || "";
}

function fmtGR(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`;
  } catch {
    return iso;
  }
}

export default function CodesView({ onExit }) {
  const [items, setItems] = useState([]);
  const [kinds, setKinds] = useState({ defaults: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sortKey, setSortKey] = useState("customer");
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");

  const fetchItems = async () => {
    setLoading(true);
    try {
      const [list, kindsResp] = await Promise.all([
        axios.get(`${API}/codes`),
        axios.get(`${API}/contracts/kinds`),
      ]);
      setItems(list.data || []);
      setKinds(kindsResp.data || { defaults: [], custom: [] });
    } catch {
      toast.error("ΣΦΑΛΜΑ ΦΟΡΤΩΣΗΣ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleSave = async (payload, id) => {
    try {
      if (id) {
        await axios.put(`${API}/codes/${id}`, payload);
        toast.success("ΑΠΟΘΗΚΕΥΤΗΚΕ");
      } else {
        await axios.post(`${API}/codes`, payload);
        toast.success("ΚΑΤΑΧΩΡΗΘΗΚΕ");
      }
      setDialogOpen(false);
      setEditing(null);
      fetchItems();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "ΣΦΑΛΜΑ ΑΠΟΘΗΚΕΥΣΗΣ");
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`ΔΙΑΓΡΑΦΗ ΚΩΔΙΚΩΝ ΓΙΑ "${c.customer}";`)) return;
    try {
      await axios.delete(`${API}/codes/${c.id}`);
      toast.success("ΔΙΑΓΡΑΦΗΚΕ");
      setDialogOpen(false);
      fetchItems();
    } catch {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  const sorted = useMemo(() => {
    const col = COLS.find((c) => c.key === sortKey) || COLS[0];
    const dir = sortDir === "asc" ? 1 : -1;
    const q = (search || "").trim().toLowerCase();
    const arr = items.filter((c) => {
      if (!q) return true;
      const hay = [
        c.customer,
        c.code,
        c.phone,
        c.notes,
        c.codes,
        itemsLabel(c),
        c.activation_date,
        c.expiry_date,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
    arr.sort((a, b) => {
      let av = col.isItems ? itemsLabel(a) : a?.[col.key];
      let bv = col.isItems ? itemsLabel(b) : b?.[col.key];
      if (col.isDate) {
        return ((av || "") > (bv || "") ? 1 : (av || "") < (bv || "") ? -1 : 0) * dir;
      }
      return String(av || "").localeCompare(String(bv || ""), "el", {
        sensitivity: "base",
      }) * dir;
    });
    return arr;
  }, [items, sortKey, sortDir, search]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-stone-100 overflow-auto" data-testid="codes-view">
      <header className="sticky top-0 z-10 bg-stone-100/95 backdrop-blur border-b border-black/5 px-6 py-3 flex flex-col gap-2">
       <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={onExit}
            data-testid="codes-back-btn"
            className="px-4 py-2.5 rounded-full flex items-center gap-2 text-sm font-bold uppercase tracking-wider border-2 bg-white/70 text-ink border-ink/15 hover:border-ink/40 active:scale-95"
          >
            <ArrowLeft size={16} weight="bold" />
            ΠΙΣΩ
          </button>
          <h2 className="font-caveat text-5xl text-ink leading-none flex items-center gap-2">
            <Key size={32} weight="bold" className="text-sky-700" />
            ΚΩΔΙΚΟΙ
          </h2>
          <span className="text-[10px] uppercase tracking-[0.2em] text-ink/55 font-bold">
            {items.length} ΕΓΓΡΑΦΕΣ
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => printCodesTable(sorted)}
            disabled={!items.length}
            data-testid="codes-print-table-btn"
            title="ΕΚΤΥΠΩΣΗ / ΕΞΑΓΩΓΗ ΣΕ PDF"
            className="px-4 py-2.5 rounded-full flex items-center gap-2 text-sm font-bold uppercase tracking-wider border-2 bg-white/70 text-ink border-ink/15 hover:border-ink/40 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FilePdf size={16} weight="bold" />
            PDF / ΕΚΤΥΠΩΣΗ
          </button>
          <button
            onClick={() => exportCodesCSV(sorted)}
            disabled={!items.length}
            data-testid="codes-export-csv-btn"
            title="ΕΞΑΓΩΓΗ ΣΕ EXCEL (CSV)"
            className="px-4 py-2.5 rounded-full flex items-center gap-2 text-sm font-bold uppercase tracking-wider border-2 bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed shadow"
          >
            <FileCsv size={16} weight="bold" />
            EXCEL
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            data-testid="codes-new-btn"
            className="px-5 py-2.5 rounded-full flex items-center gap-2 text-sm font-bold uppercase tracking-wider border-2 bg-sky-600 text-white border-sky-700 hover:bg-sky-700 active:scale-95 shadow"
          >
            <Plus size={16} weight="bold" />
            ΝΕΑ ΕΓΓΡΑΦΗ
          </button>
        </div>
       </div>
       <div className="flex items-center gap-2 pt-1">
        <MagnifyingGlass size={18} weight="bold" className="text-ink/45" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="codes-search-input"
          placeholder="ΑΝΑΖΗΤΗΣΗ ΣΕ ΠΕΛΑΤΗ, ΚΩΔΙΚΟ, ΤΗΛΕΦΩΝΟ, ΣΗΜΕΙΩΣΕΙΣ..."
          className="flex-1 max-w-xl px-4 py-2 rounded-full border-2 border-ink/15 bg-white focus:border-ink/40 outline-none text-sm font-arial uppercase tracking-wide placeholder:text-ink/35 placeholder:normal-case placeholder:tracking-normal"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            data-testid="codes-search-clear"
            className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-ink/60 hover:text-ink hover:bg-black/5"
          >
            × ΚΑΘΑΡΙΣΜΟΣ
          </button>
        )}
        <span className="text-[10px] uppercase tracking-[0.2em] text-ink/45 font-bold tabular-nums">
          {sorted.length} / {items.length}
        </span>
       </div>
      </header>

      <main className="px-6 py-6">
        {loading ? (
          <p className="text-center text-ink-muted font-caveat text-4xl">ΦΟΡΤΩΣΗ...</p>
        ) : (
          <div
            className="rounded-xl border border-black/10 bg-white overflow-x-auto shadow-sm"
            data-testid="codes-table"
          >
            <table className="w-full text-sm border-collapse">
              <thead className="bg-ink text-white sticky top-0">
                <tr>
                  {COLS.map((c) => {
                    const active = c.key === sortKey;
                    const Icon = !active
                      ? CaretUpDown
                      : sortDir === "asc"
                      ? CaretUp
                      : CaretDown;
                    return (
                      <th
                        key={c.key}
                        onClick={() => toggleSort(c.key)}
                        data-testid={`codes-table-header-${c.key}`}
                        style={
                          c.width
                            ? { width: c.width, minWidth: c.width }
                            : c.minWidth
                            ? { minWidth: c.minWidth }
                            : undefined
                        }
                        className="px-3 py-2.5 select-none cursor-pointer uppercase tracking-wider text-xs font-black border-r border-white/10 last:border-r-0 hover:bg-ink/85 transition-colors text-left"
                      >
                        <div className="inline-flex items-center gap-1">
                          <span>{c.label}</span>
                          <Icon
                            size={12}
                            weight={active ? "bold" : "regular"}
                            className={active ? "text-amber-300" : "text-white/40"}
                          />
                        </div>
                      </th>
                    );
                  })}
                  <th className="px-3 py-2.5 text-right uppercase tracking-wider text-xs font-black w-[140px]">
                    ΕΝΕΡΓΕΙΕΣ
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 && (
                  <tr>
                    <td
                      colSpan={COLS.length + 1}
                      className="px-3 py-10 text-center italic text-ink/40 text-base"
                    >
                      ΧΩΡΙΣ ΕΓΓΡΑΦΕΣ
                    </td>
                  </tr>
                )}
                {sorted.map((c, i) => (
                  <tr
                    key={c.id}
                    data-testid={`codes-table-row-${c.id}`}
                    onClick={() => {
                      setEditing(c);
                      setDialogOpen(true);
                    }}
                    className={`cursor-pointer ${
                      i % 2 === 0 ? "bg-white" : "bg-black/[0.018]"
                    } hover:bg-amber-50/60 border-t border-black/5`}
                  >
                    {COLS.map((col) => {
                      let display;
                      const raw = col.isItems ? itemsLabel(c) : c?.[col.key];
                      if (col.isDate) display = fmtGR(raw);
                      else display = raw;
                      return (
                        <td
                          key={col.key}
                          style={
                            col.width
                              ? { width: col.width, minWidth: col.width }
                              : col.minWidth
                              ? { minWidth: col.minWidth }
                              : undefined
                          }
                          className={`px-3 py-2 border-r border-black/5 last:border-r-0 text-left ${
                            col.wide ? "max-w-[240px]" : ""
                          } ${col.codes ? "font-mono text-xs" : ""}`}
                        >
                          <div
                            className={`truncate ${
                              col.wide ? "line-clamp-2 whitespace-pre-line" : ""
                            }`}
                            title={String(display || "")}
                          >
                            {display || <span className="text-ink/25">—</span>}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => {
                            setEditing(c);
                            setDialogOpen(true);
                          }}
                          data-testid={`code-edit-${c.id}`}
                          title="ΑΝΟΙΓΜΑ"
                          className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-ink/30 bg-white text-ink hover:bg-stone-50 active:scale-95 inline-flex items-center gap-1"
                        >
                          <Pencil size={13} weight="bold" />
                          ΑΝΟΙΓΜΑ
                        </button>
                        <button
                          onClick={() => printCodeCard(c)}
                          data-testid={`code-print-${c.id}`}
                          title="ΕΚΤΥΠΩΣΗ ΚΑΡΤΑΣ"
                          className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-ink/30 bg-white text-ink hover:bg-stone-50 active:scale-95 inline-flex items-center gap-1"
                        >
                          <Printer size={13} weight="bold" />
                          ΕΚΤΥΠ.
                        </button>
                        <button
                          onClick={() => handleDelete(c)}
                          data-testid={`code-delete-${c.id}`}
                          title="ΔΙΑΓΡΑΦΗ"
                          className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-red-300 bg-white text-red-700 hover:bg-red-50 active:scale-95 inline-flex items-center gap-1"
                        >
                          <Trash size={13} weight="bold" />
                          ΔΙΑΓΡ.
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <ContractDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditing(null);
        }}
        initial={editing}
        kinds={kinds}
        onSave={handleSave}
        onDelete={handleDelete}
        onPrint={printCodeCard}
        variant="code"
      />
    </div>
  );
}
